// LibraryDropConfirmPopover — small confirm UI shown after a library item is
// dropped onto the Draft Canvas when the workspace + item combo is eligible
// for an Amazon Associates affiliate link. The user can opt-in (default) or
// out per insert; their last choice is remembered in localStorage.

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { readAmazonLinkDefault, writeAmazonLinkDefault } from "./libraryDropDefaults";

export type LibraryDropConfirmPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pixel coordinates (viewport) where the drop landed. */
  anchorPoint: { x: number; y: number } | null;
  itemTitle: string;
  onConfirm: (includeAffiliateLink: boolean) => void;
  onCancel: () => void;
};

export const LibraryDropConfirmPopover = ({
  open,
  onOpenChange,
  anchorPoint,
  itemTitle,
  onConfirm,
  onCancel,
}: LibraryDropConfirmPopoverProps) => {
  const [includeLink, setIncludeLink] = useState<boolean>(true);

  useEffect(() => {
    if (open) setIncludeLink(readAmazonLinkDefault());
  }, [open]);

  const anchorStyle: React.CSSProperties = anchorPoint
    ? {
        position: "fixed",
        left: anchorPoint.x,
        top: anchorPoint.y,
        width: 1,
        height: 1,
        pointerEvents: "none",
      }
    : { display: "none" };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <span style={anchorStyle} aria-hidden="true" />
      </PopoverAnchor>
      <PopoverContent className="w-80" align="start" sideOffset={8}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium leading-tight">Insert as quote</p>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{itemTitle}</p>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="library-drop-amazon-link"
              checked={includeLink}
              onCheckedChange={(v) => setIncludeLink(v === true)}
            />
            <Label
              htmlFor="library-drop-amazon-link"
              className="cursor-pointer text-xs font-normal leading-snug"
            >
              Make book title an Amazon affiliate link
            </Label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                writeAmazonLinkDefault(includeLink);
                onConfirm(includeLink);
              }}
            >
              Insert
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
